import { isBefore, parseISO, startOfDay } from '@if/core/date'
import { t } from '@if/core/i18n'
import {
  EFinancingRefusalMessage,
  EPaymentFrequency,
  EPaymentPlan,
  EWorksheetDecision,
} from '@if/core/types'
import { useSetBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { CreditApplication, WorksheetDecision } from '@src/data/types'
import { IFinanceBaseWorksheet } from '@src/data/types/IFinanceBaseWorksheetSchema'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  computeTotalObligation,
  getMaxPaymentAmountWithAgentLeeway,
  isPaymentAmountForFrequencyValid,
  TotalObligationComputedInfo,
} from './viewWorksheet-selectors'

export const useCantApproveReasonCodes = (
  worksheetNormRefusalMessages: string[],
  areAllTasksRequiredForCVTCompleted: boolean,
  areAllDocumentsRequiredForCVTApproved: boolean,
  incomeConfirmed: boolean,
  creditApplication: CreditApplication | null,
  worksheet: IFinanceBaseWorksheet | null,
) => {
  const computeCantApproveReasonCodes = useCallback(() => {
    const message: EFinancingRefusalMessage[] = []
    if (worksheetNormRefusalMessages.length > 0) message.push(EFinancingRefusalMessage.NormRefusalMessage)
    if (!areAllTasksRequiredForCVTCompleted) message.push(EFinancingRefusalMessage.TaskIncomplete)
    if (!areAllDocumentsRequiredForCVTApproved) message.push(EFinancingRefusalMessage.MissingRequiredDocument)
    if (!incomeConfirmed) message.push(EFinancingRefusalMessage.NotAllIcomeConfirmed)
    if (
      !creditApplication?.applicant.existingCustomerReference ||
      (creditApplication?.coApplicant && !creditApplication?.coApplicant.existingCustomerReference)
    )
      message.push(EFinancingRefusalMessage.MissingExistingCustomerReference)
    if (worksheet?.deliveryOn) {
      const localDeliveryDate = parseISO(worksheet.deliveryOn)
      if (isBefore(startOfDay(localDeliveryDate), startOfDay(new Date()))) {
        message.push(EFinancingRefusalMessage.DeliveryDateInPast)
      }
    }

    return message
  }, [
    worksheetNormRefusalMessages.length,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    incomeConfirmed,
    creditApplication,
    worksheet,
  ])

  return computeCantApproveReasonCodes
}

export const useDialogDecisionDefaultValue = (
  creditApplication: CreditApplication,
  worksheet: IFinanceBaseWorksheet,
  areAllTasksRequiredForCVTCompleted: boolean,
  areAllDocumentsRequiredForCVTApproved: boolean,
  incomeConfirmed: boolean,
) => {
  const computeCantApproveReasonCodes = useCantApproveReasonCodes(
    worksheet?.finalDecision?.refusalMessages?.map((x) => x.id) || [],
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    incomeConfirmed,
    creditApplication,
    worksheet,
  )

  const dialogDecisionDefaultValue = useMemo(() => {
    const ret: WorksheetDecision = {
      decision: worksheet?.finalDecision?.decision,
      otherReason: worksheet?.finalDecision?.otherReason ?? '',
      refusalReasonCodes: worksheet?.finalDecision?.refusalMessages?.map((x) => x.id) ?? [],
      cantApproveReasonCodes: computeCantApproveReasonCodes(),
      maxPmtAmount: worksheet?.fundingThresholdExtend?.maxPmtAmount ?? 0,
      maxTermDuration: worksheet?.fundingThresholdExtend?.maxTermDuration ?? 0,
      maxTotalAmountFinanced: worksheet?.fundingThresholdExtend?.maxTotalAmountFinanced ?? 0,
      id: undefined,
      decidorFullName: null,
      decidorId: null,
      refusalMessages: [],
      warningMessages: [],
    }

    if (worksheet?.fundingThresholdExtend) {
      ret.decision = EWorksheetDecision.ApprovedWithCondition
    }

    return ret
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    creditApplication,
    worksheet,
    computeCantApproveReasonCodes,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    incomeConfirmed,
  ])

  return dialogDecisionDefaultValue
}

export const useMaxPaymentError = (
  frequency: EPaymentFrequency | undefined,
  paymentAmountForFrequency: number | undefined,
  maxPaymentAmount: number | undefined,
  validateMaxPaymentAmountInWorksheet: boolean,
) => {
  const [error, setError] = useState<string | null>(null);
  const setBusinessErrors = useSetBusinessErrors();

  useEffect(() => {
    if (!validateMaxPaymentAmountInWorksheet) {
      setBusinessErrors([]);
      setError(null);
      return;
    }

    if (
      maxPaymentAmount != null &&
      paymentAmountForFrequency != null &&
      frequency &&
      !isPaymentAmountForFrequencyValid(frequency, paymentAmountForFrequency, maxPaymentAmount)
    ) {
      const errorMsg = t('common.errors.maxPaymentExceedsMaximumAllowed', {
        maxPayment: maxPaymentAmount,
      });
      setBusinessErrors([
        {
          message: errorMsg,
        },
      ]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setError(errorMsg);
    } else {
      setBusinessErrors([]);
      setError(null);
    }
  }, [frequency, paymentAmountForFrequency, maxPaymentAmount, setBusinessErrors, validateMaxPaymentAmountInWorksheet]);

  return error;
};

export const useMaxPaymentAmountWithAgentLeeway = (paymentPlanId: EPaymentPlan, maxPaymentAmount: number) => {
  return useMemo(
    () => getMaxPaymentAmountWithAgentLeeway(paymentPlanId, maxPaymentAmount),
    [paymentPlanId, maxPaymentAmount],
  )
}

export const useViewTotalObligation = (
  amountRequested: number | null | undefined,
  computedInfo?: TotalObligationComputedInfo,
): number =>
  useMemo(
    () => computeTotalObligation(amountRequested ?? 0, computedInfo),
    [amountRequested, computedInfo],
  )
